// ============================================================================
// gating.ts — a faithful TypeScript MIRROR of the real device pipeline's
// alert-vs-ignore decision (kinsafe_local/pipeline.py :: gate_by_sensitivity).
// ============================================================================
// WHY this exists: the try-before-install demo (PRD §1.9-B, ROADMAP D5) lets a
// prospect drag the SAME action / body-part / graphicness sliders the product
// ships and watch a gallery reclassify live. There is NO backend and NO
// arbitrary upload in the demo, so we cannot call the real Python pipeline.
// Instead we replicate its PURE gating function here and drive it off a
// PRECOMPUTED dataset (demoData.ts) of {flagged_parts, flagged_actions, score}
// per tile. The logic below is a line-for-line port — keep it in sync with
// pipeline.gate_by_sensitivity so the demo never lies about how the product
// behaves.
//
// SENSITIVITY SCALE — the integer axis used here is the SAME 0..3 the product
// exposes (customer-app/src/screens/Sliders.tsx) AND the same integer the
// Python gate consumes. Mapping (higher int = stricter, lower = catch more):
//   0 Off       — this axis contributes nothing.
//   1 strict    — alert only on the hardest signal on this axis.
//   2 standard  — alert on any flagged signal on this axis. (DEFAULT)
//   3 lenient   — any flagged signal OR fallback-nudity OR a high gate score.
//
// NOTE on the product UI labels: the customer app labels tick 1 as "High
// sensitivity" (most alerts) and tick 3 as "Explicit only" (fewest). That UI
// label order is the inverse of the raw pipeline int here. The demo's slider
// component owns that presentation; this module speaks ONLY the raw pipeline
// integers so it matches gate_by_sensitivity exactly. See demoData / the
// playground for how UI position maps to these ints.
// ============================================================================

// The high body-part class the STRICT body-part level requires
// (pipeline.py _GENITAL_PARTS).
export const GENITAL_PARTS = [
  "vagina/vulva",
  "penis/testicles",
  "anus",
] as const;

// A gatekeeper score at/above this is treated as "obviously explicit" for the
// lenient backstop and the BOTH-OFF safety net (pipeline.py HIGH_GATEKEEPER_SCORE).
export const HIGH_GATEKEEPER_SCORE = 0.9;

// The hardest, unambiguous sex acts the STRICT action level requires
// (pipeline.py gate_by_sensitivity `hard` tuple).
const HARD_ACTIONS = [
  "fellatio (blow job)",
  "cunnilingus (oral sex on female)",
  "sexual intercourse",
  "masturbation",
  "oral sex",
];

export interface Classification {
  // The structured signals _classify_description already produces on the device.
  parts: string[];
  actions: string[];
  // The classifier's own is_explicit verdict — used ONLY to detect the
  // fallback-nudity case (is_explicit true with NO named part/act).
  isExplicit: boolean;
}

export interface GateInputs {
  classification: Classification;
  gatekeeperScore: number;
  actionSensitivity: number; // 0 off | 1 strict | 2 standard | 3 lenient
  bodypartSensitivity: number; // 0 off | 1 strict | 2 standard | 3 lenient
  highGatekeeperScore?: number;
}

export interface GateResult {
  // The final alert-vs-ignore verdict (the function's bool return).
  alert: boolean;
  // The per-axis contributions + the safety net, surfaced so the demo can SHOW
  // a prospect *why* a tile flagged (the Python fn only returns the bool; the
  // demo wants the reasoning to make the gating legible).
  actionAlert: boolean;
  bodypartAlert: boolean;
  safetyNet: boolean;
}

// gate_by_sensitivity — PURE alert-vs-ignore decision. Direct port of the
// Python function of the same name (pipeline.py). No I/O, fully deterministic,
// so it unit-tests against synthetic classifications exactly like the original.
export function gateBySensitivity(input: GateInputs): GateResult {
  const c = input.classification;
  const parts = c.parts ?? [];
  const actions = c.actions ?? [];
  const isExplicit = Boolean(c.isExplicit);
  const score = Number.isFinite(input.gatekeeperScore)
    ? input.gatekeeperScore
    : 0;
  const a = Math.trunc(input.actionSensitivity);
  const b = Math.trunc(input.bodypartSensitivity);
  const highThreshold = input.highGatekeeperScore ?? HIGH_GATEKEEPER_SCORE;

  // The fallback-nudity signal: the classifier called it explicit but named NO
  // specific part and NO specific act => it fired on general nudity language.
  // The LENIENT end of each axis honors this; stricter levels require a NAMED
  // part/act. (pipeline.py: fallback_nudity = is_explicit and not parts and not actions)
  const fallbackNudity = isExplicit && parts.length === 0 && actions.length === 0;
  const highScore = score >= highThreshold;

  // ---- ACTION axis -------------------------------------------------------
  let actionAlert = false;
  if (a >= 1) {
    const hasAction = actions.length > 0;
    if (a === 1) {
      // strict: only the hardest, unambiguous sex acts.
      actionAlert = actions.some((act) => HARD_ACTIONS.includes(act));
    } else if (a === 2) {
      // standard: any flagged action.
      actionAlert = hasAction;
    } else {
      // a >= 3 lenient: catch more.
      actionAlert = hasAction || fallbackNudity || highScore;
    }
  }

  // ---- BODY-PART axis (only ever tightens — requires NAMED parts) --------
  let bodypartAlert = false;
  if (b >= 1) {
    const hasPart = parts.length > 0;
    if (b === 1) {
      // strict: only genital-class named parts.
      bodypartAlert = parts.some((p) =>
        (GENITAL_PARTS as readonly string[]).includes(p),
      );
    } else if (b === 2) {
      // standard: any flagged part (incl. breasts/nipples/buttocks).
      bodypartAlert = hasPart;
    } else {
      // b >= 3 lenient: catch more.
      bodypartAlert = hasPart || fallbackNudity || highScore;
    }
  }

  if (actionAlert || bodypartAlert) {
    return { alert: true, actionAlert, bodypartAlert, safetyNet: false };
  }

  // Safety net: BOTH axes contributed nothing. A high gatekeeper score must
  // still alert so the sliders can never silently suppress the obviously-
  // explicit frame. (pipeline.py: returns high_score here.)
  return {
    alert: highScore,
    actionAlert: false,
    bodypartAlert: false,
    safetyNet: highScore,
  };
}
